import type { FC, ReactElement } from 'react';

import { useRedirectToLobbyCallbackConnected } from '../../hooks/useRdirectToLobbyCallbackConnected';
import { Logger } from '../../logger/Logger';
import { CoreDispatch, useCoreDispatch } from '../../store/CoreStore';
import { usePopupSlice } from '../../store/slices/popup/selectors';
import { popupActions } from '../../store/slices/popup/slice';

import { AutoplayModifiedConnected, autoplayModifiedConnectedOnClose } from './content/AutoplayModifiedConnected';
import { AutoplayStoppedConnected } from './content/AutoplayStoppedConnected';
import { BetNotAllowedConnected } from './content/BetNotAllowedConnected';
import { BetNotOnTimeConnected } from './content/BetNotOnTimeConnected';
import { ConnectionLostConnected, connectionLostOnClose } from './content/ConnectionLostConnected';
import { DontLeaveRoundInProgressConnected } from './content/DontLeaveRoundInProgressConnected';
import { ErrorConnected } from './content/ErrorConnected';
import { ExitConfirmationConnected } from './content/ExitConfirmationConnected';
import { GenericErrorConnected } from './content/GenericErrorContentConnected';
import { InactivityConnected, inactivityConnectedOnClose } from './content/InactivityConnected';
import { InsufficientFundsConnected } from './content/InsufficientFundsConnected';
import { loggedOutClose, LoggedOutConnected } from './content/LoggedOutConnected';
import { NicknameEnterConnected } from './content/NicknameEnterConnected';
import { PlayerLimitConnected } from './content/PlayerLimitConnected';
import { PrivateTableByInvitationConnected } from './content/PrivateTableByInvitationConnected';
import { PrivateTableOccupiedConnected } from './content/PrivateTableOccupiedConnected';
import { PromoRoundLimitConnected } from './content/PromoRoundLimitConnected';
import { RealityCheckConnected } from './content/RealityCheckConnected';
import { ReconnectingPopup } from './content/Reconnecting';
import { ServerMessageConnected } from './content/ServerMessageConnected';
import { SessionBalanceConnected } from './content/SessionBalanceConnected';
import { SessionBalanceErrorConnected } from './content/SessionBalanceErrorConnected';
import { TableClosedConnected } from './content/TableClosedConnected';
import { VideoIsDisabledConnected } from './content/VideoIsDisabledConnected';
import { VideoStreamQualityConnected } from './content/VideoStreamQualityConnected';
import { VipTableLowBalanceConnected } from './content/VipTableLowBalanceConnected';
import { Popup } from './Popup';
import { SessionExpiredConnected } from './sessionExpired/SessionExpiredPopupConnected';
import { PopupType } from './types';

export interface PopupConfigInterface {
  content: ReactElement;
  headerLess?: boolean;
  onClose?: (dispatch: CoreDispatch) => void;
}

const popupsMap: Record<PopupType, PopupConfigInterface> = {
  [PopupType.AutoplayModified]: { content: <AutoplayModifiedConnected />, onClose: autoplayModifiedConnectedOnClose },
  [PopupType.AutoplayStopped]: { content: <AutoplayStoppedConnected /> },
  [PopupType.BetNotAllowed]: { content: <BetNotAllowedConnected /> },
  [PopupType.BetNotOnTime]: { content: <BetNotOnTimeConnected /> },
  [PopupType.ConnectionLost]: { content: <ConnectionLostConnected />, onClose: connectionLostOnClose },
  [PopupType.CustomPopup]: { content: <ServerMessageConnected />, headerLess: true },
  [PopupType.DontLeaveRoundInProgress]: { content: <DontLeaveRoundInProgressConnected />, headerLess: true },
  [PopupType.ExitConfirmation]: { content: <ExitConfirmationConnected />, headerLess: true },
  [PopupType.GenericError]: { content: <GenericErrorConnected />, headerLess: true },
  [PopupType.SessionExpired]: { content: <SessionExpiredConnected />, headerLess: true },
  [PopupType.Inactivity]: { content: <InactivityConnected />, onClose: inactivityConnectedOnClose },
  [PopupType.InsufficientFunds]: { content: <InsufficientFundsConnected /> },
  [PopupType.LoggedOut]: { content: <LoggedOutConnected />, onClose: loggedOutClose },
  [PopupType.NicknameEnter]: { content: <NicknameEnterConnected />, headerLess: true },
  [PopupType.PlayerLimit]: { content: <PlayerLimitConnected /> },
  [PopupType.RealityCheck]: { content: <RealityCheckConnected />, headerLess: true },
  [PopupType.Reconnecting]: { content: <ReconnectingPopup />, headerLess: true },
  [PopupType.SessionBalance]: { content: <SessionBalanceConnected /> },
  [PopupType.TableClosed]: { content: <TableClosedConnected />, headerLess: true },
  [PopupType.PrivateTableOccupied]: { content: <PrivateTableOccupiedConnected />, headerLess: true },
  [PopupType.VipTableLowBalance]: { content: <VipTableLowBalanceConnected />, headerLess: true },
  [PopupType.VideoIsDisabled]: { content: <VideoIsDisabledConnected /> },
  [PopupType.VideoStreamQuality]: { content: <VideoStreamQualityConnected /> },
  [PopupType.SessionBalanceError]: { content: <SessionBalanceErrorConnected /> },
  [PopupType.PromoRoundLimit]: { content: <PromoRoundLimitConnected /> },
  [PopupType.Error]: { content: <ErrorConnected /> },
  [PopupType.ByInvitation]: { content: <PrivateTableByInvitationConnected />, headerLess: true },
};

export interface PopupConnectedProps {
  additionalPopups?: Record<string, PopupConfigInterface>;
}

export const PopupConnected: FC<PopupConnectedProps> = ({ additionalPopups }) => {
  const dispatch = useCoreDispatch();
  const currentPopup = usePopupSlice.currentPopup();
  const redirectToLobby = useRedirectToLobbyCallbackConnected();

  if (currentPopup) {
    Logger.logEvent('click', 'showPopup', Date.now(), 1, currentPopup, false);
  }

  const allPopups: Record<string, PopupConfigInterface> = { ...popupsMap, ...additionalPopups };

  const popupContent = currentPopup ? allPopups[currentPopup] : null;

  const content = popupContent?.content;

  const handleClose = (): void => {
    dispatch(popupActions.dismissPopup(currentPopup!));
    if (popupContent?.onClose) {
      popupContent.onClose(dispatch);
    }
    // TODO: This is need because right now navigation is via hook. We need to create event-driven navigation controller to be able to trigger navigation events from plain components
    if (currentPopup === PopupType.SessionBalance || currentPopup === PopupType.SessionBalanceError) {
      redirectToLobby();
    }
  };

  return content && <Popup content={content} handleClose={handleClose} headerLess={popupContent?.headerLess} />;
};
